// FILE: src/pages/IssueReportPage.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
} from "@mui/material";
import API from "../api";

export default function IssueReportPage() {
  // date filters
  const [startDate, setStart] = useState("");
  const [endDate, setEnd] = useState("");
  // account & additional filters
  const [accountFilter, setAccount] = useState("ALL");
  const [accounts, setAccounts] = useState([]);
  const [managerFilter, setManager] = useState("ALL");
  const [tillUserFilter, setTillUser] = useState("ALL");
  // report data & pagination
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // default to current month
  useEffect(() => {
    const now = new Date();
    const f = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString().split("T")[0];
    const l = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString().split("T")[0];
    setStart(f);
    setEnd(l);
  }, []);

  // fetch accounts for filter
  useEffect(() => {
    API.get("/accounts", { params: { limit: 1000000 } })
      .then(res => setAccounts(res.data.accounts || []))
      .catch(() => {});
  }, []);

  // fetch (or append) report pages
  const fetchReport = (pg = 1, append = false) => {
    setLoading(true);
    setError("");
    API.get("/advance-reports/issue", {
      params: {
        startDate,
        endDate,
        account: accountFilter,
        shiftManager: managerFilter,
        tillUser: tillUserFilter,
        page: pg,
        limit: 50,
      },
    })
      .then(({ data }) => {
        setRows(prev => (append ? [...prev, ...data.rows] : data.rows || []));
        setPage(data.pagination.currentPage);
        setTotalPages(data.pagination.totalPages);
      })
      .catch(err => {
        setError(err.response?.data?.message || "Failed to load");
      })
      .finally(() => setLoading(false));
  };

  // handlers
  const handleGenerate = () => fetchReport(1, false);
  const handleLoadMore = () => {
    if (page < totalPages) fetchReport(page + 1, true);
  };

  const handleExport = () => {
    const q = new URLSearchParams({
      startDate,
      endDate,
      account: accountFilter,
      shiftManager: managerFilter,
      tillUser: tillUserFilter,
    }).toString();

    API.get(`/advance-reports/issue/export?${q}`, { responseType: "blob" })
      .then(res => {
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "issue-report.csv";
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError("Export failed"));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Issue Report
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={e => setStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={e => setEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            options={[{ _id: "ALL", accountName: "All", accountNo: "" }, ...accounts]}
            getOptionLabel={o =>
              o._id === "ALL" ? "All Accounts" : `${o.accountName} (${o.accountNo})`
            }
            value={
              accountFilter === "ALL"
                ? { _id: "ALL", accountName: "All", accountNo: "" }
                : accounts.find(a => a._id === accountFilter) || null
            }
            onChange={(_, nv) => setAccount(nv ? nv._id : "ALL")}
            renderInput={params => <TextField {...params} label="Account" size="small" />}
            sx={{ width: 250 }}
          />
          <TextField
            label="Filter by Shift Manager"
            size="small"
            value={managerFilter}
            onChange={e => setManager(e.target.value)}
            placeholder="Name…"
          />
          <TextField
            label="Filter by Till User"
            size="small"
            value={tillUserFilter}
            onChange={e => setTillUser(e.target.value)}
            placeholder="Name…"
          />
          <Button variant="contained" onClick={handleGenerate}>
            Generate Report
          </Button>
          <Button variant="outlined" onClick={handleExport}>
            Export CSV
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper sx={{ overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Shift Manager</TableCell>
                  <TableCell>Till Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>{r.account}</TableCell>
                    <TableCell>{r.category}</TableCell>
                    <TableCell>{r.note}</TableCell>
                    <TableCell>{r.shiftManager}</TableCell>
                    <TableCell>{r.tillUsers}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Box sx={{ textAlign: "center", mt: 2 }}>
            {page < totalPages && (
              <Button variant="contained" onClick={handleLoadMore} disabled={loading}>
                {loading ? "Loading…" : "Load More"}
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
